import { Product } from "@/types";
import { router, usePage } from "@inertiajs/react";
import { useMemo, useState } from "react";

type UseFilteredProducts = {
  updateUrl?: boolean;
};

export default function useFilteredProducts(
  products: Product[],
  options: UseFilteredProducts = {}
) {
  const page = usePage();
  const queryParams = useMemo(() => {
    const url = new URL(page.url, window.location.origin);
    return Object.fromEntries(url.searchParams.entries());
  }, [page.url]);

  const [filter, setBaseFilter] = useState<{
    product_type: string;
    state: string;
    theme: string;
  }>(() => {
    if (options.updateUrl) {
      return {
        product_type: queryParams.product_type ?? "all",
        state: queryParams.state ?? "all",
        theme: queryParams.theme ?? "all",
      };
    }
    return { product_type: "all", state: "all", theme: "all" };
  });
  const [search, setSearch] = useState<string>(() => {
    if (options.updateUrl) {
      return queryParams.search ?? "";
    }

    return "";
  });

  const filteredProducts = useMemo(() => {
    let filteredProducts = products;

    if (search) {
      filteredProducts = filteredProducts.filter(
        (product) =>
          product.name.toLowerCase().includes(search.toLowerCase()) ||
          product.system_code.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (filter.theme !== "all") {
      filteredProducts = filteredProducts.filter(
        (product) => product.theme && product.theme.id === filter.theme
      );
    }

    if (filter.product_type !== "all") {
      filteredProducts = filteredProducts.filter(
        (product) => product.product_type === filter.product_type
      );
    }

    if (filter.state !== "all") {
      filteredProducts = filteredProducts.filter(
        (product) => product.state === filter.state
      );
    }

    return filteredProducts;
  }, [filter, search, products]);

  function setFilter(values: Partial<typeof filter>): void {
    setBaseFilter((oldValues) => ({ ...oldValues, ...values }));

    if (options.updateUrl) {
      const url = new URL(window.location.href);

      Object.entries(values).forEach(([key, value]) => {
        if (value === "" || value === null || value === undefined) {
          url.searchParams.delete(key);
        } else {
          url.searchParams.set(key, value);
        }
      });

      router.replace({
        url: url.pathname + url.search,
        preserveState: true, // Prevents losing local state component-side
      });
    }
  }

  return {
    filter,
    setFilter,
    search,
    setSearch,
    filteredProducts,
  };
}
